/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react'

import Cookies from 'js-cookie'

import * as anchor from '@coral-xyz/anchor'
import { Program } from '@coral-xyz/anchor'
import {
  SolCatContext,
  V2Context,
  StakingClient,
  SolCatStakingIDL,
  V2StakingIDL,
  SolCatStakingType,
  V2StakingType,
} from '@catsolhat/staking-sdk'
import { useWallet } from '@solana/wallet-adapter-react'
import { Connection, Keypair, PublicKey, Transaction } from '@solana/web3.js'

import { getSignatureForSignIn } from '../../lib/verify'
import { ACCESS_TOKEN, MAX_AGE_ACCESS_TOKEN, MAX_AGE_REFRESH_TOKEN, REFRESH_TOKEN } from '../../utils/apis'
import { signInApi } from '../../utils/apis/staking/signIn'
import { Project } from '../../types'
import { MultiVersionStakingClient } from '../../utils/multi-version-staking-client'
import { isSolcatTokenAddress } from '../../utils/helpers'
import { JitoJsonRpcClient } from 'jito-js-rpc'
import { useLocalStorage } from 'usehooks-ts'

const defaultSolanaContext = {
  connection: null as Connection | null,
  provider: null as anchor.AnchorProvider | null,
  program: null as Program<SolCatStakingType> | Program<V2StakingType> | null,
  ctx: null as SolCatContext | V2Context | null,
  stakingClient: null as StakingClient | null,
  mintPublicKey: new PublicKey('11111111111111111111111111111111'),
  publicKey: null as PublicKey | null,
  signTransaction: null as ((tx: Transaction) => Promise<Transaction>) | null,
  connected: false,
  connect: null as (() => Promise<void>) | null,
  disconnect: null as (() => Promise<void>) | null,
  signInWithMessage: null as (() => Promise<void>) | null,
  disconnecting: null as boolean | null,
  currentProject: null as Project | null,
  setCurrentProject: null as ((project: Project) => void) | null,
  isUserPublicKey: null as boolean | null,
  usingJitoValidator: false,
  setUsingJitoValidator: null as ((usingJitoValidator: boolean) => void) | null,
  jitoClient: null as any | null,
  jitoTipAmount: 0.0001,
}

const SolanaContext = React.createContext(defaultSolanaContext)

interface SolanaProviderProps {
  children: ReactNode
}
export const SolanaProvider: React.FC<SolanaProviderProps> = ({ children }) => {
  const {
    connect,
    disconnect,
    connected,
    publicKey: userPublicKey,
    signTransaction,
    signAllTransactions,
    signMessage,
    disconnecting,
  } = useWallet()

  const [currentProject, setCurrentProject] = useState<Project | null>(null)
  const [isUserPublicKey, setIsUserPublicKey] = useState(false)

  const wallet = { signMessage, publicKey: userPublicKey }

  const signInWithMessage = async () => {
    try {
      const signatureInfo = await getSignatureForSignIn(wallet)

      if (!signatureInfo || !userPublicKey) return
      const response = await signInApi(userPublicKey.toBase58(), signatureInfo.msg, signatureInfo.signature)
      if (response.success) {
        Cookies.set(ACCESS_TOKEN, response.data.access_token, {
          expires: MAX_AGE_ACCESS_TOKEN,
        })
        Cookies.set(REFRESH_TOKEN, response.data.session_token, {
          expires: MAX_AGE_REFRESH_TOKEN,
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('error', error)
    }
  }

  // Random keypair for reading data from the blockchain
  const secretKey = Uint8Array.from([
    251, 148, 14, 94, 155, 217, 189, 21, 81, 107, 24, 9, 45, 87, 164, 161, 202, 187, 143, 214, 97, 100, 20, 159, 242,
    202, 243, 194, 149, 149, 41, 119, 168, 4, 19, 125, 105, 92, 17, 124, 195, 70, 200, 180, 235, 89, 22, 132, 69, 119,
    41, 94, 13, 175, 197, 133, 133, 21, 233, 191, 110, 217, 178, 192,
  ])
  const defaultKeypair = Keypair.fromSecretKey(secretKey)
  const [publicKey, setPublicKey] = useState<PublicKey>(defaultKeypair.publicKey)

  const connectionUrl = process.env.NEXT_PUBLIC_SOLANA_CONNECTION_URL
  if (!connectionUrl) {
    throw new Error('NEXT_PUBLIC_SOLANA_CONNECTION_URL is not defined')
  }
  const connection = useMemo(() => new Connection(connectionUrl, 'confirmed'), [connectionUrl])

  const provider = useMemo(() => {
    return new anchor.AnchorProvider(connection, {
      publicKey: publicKey,
      signTransaction: signTransaction!,
      signAllTransactions: signAllTransactions!,
    })
  }, [connection, publicKey, signAllTransactions, signTransaction])

  const programV1 = new Program<SolCatStakingType>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    SolCatStakingIDL as any,
    provider,
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const programV2 = new Program<V2StakingType>(V2StakingIDL as any, provider)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const solCatCtx = SolCatContext.withProvider(provider)
  const v2Ctx = V2Context.withProvider(provider)

  // const stakingClient = new StakingClient(solCatCtx, v2Ctx, new PDA(programV1.programId), new PDA(programV2.programId))
  const stakingClient = new MultiVersionStakingClient(solCatCtx, v2Ctx, programV1, programV2)

  const program = isSolcatTokenAddress(currentProject?.address) ? programV1 : programV2
  console.log('program', program.programId.toBase58())
  const ctx = isSolcatTokenAddress(currentProject?.address) ? solCatCtx : v2Ctx

  const mintAddress = currentProject?.address || '11111111111111111111111111111111'

  const mintPublicKey = new PublicKey(mintAddress)

  const [usingJitoValidator, setUsingJitoValidator] = useLocalStorage('usingJitoValidator', false)
  const jitoClient = new JitoJsonRpcClient('https://mainnet.block-engine.jito.wtf/api/v1', '')
  const jitoTipAmount = Number(process.env.NEXT_PUBLIC_MAX_JITO_TIP_AMOUNT) || 0.0001 // SOL

  useEffect(() => {
    if (connected && userPublicKey) {
      setPublicKey(userPublicKey) // change default pubkey to user pubkey when connected
      setIsUserPublicKey(true)
    } else {
      setIsUserPublicKey(false)
    }
  }, [connected, userPublicKey])

  const value = {
    connection,
    provider,
    program,
    ctx,
    stakingClient,
    mintPublicKey,
    publicKey,
    signTransaction: signTransaction ?? null,
    connected,
    connect,
    disconnect,
    signInWithMessage,
    disconnecting,
    currentProject,
    setCurrentProject,
    isUserPublicKey,
    usingJitoValidator,
    setUsingJitoValidator,
    jitoClient,
    jitoTipAmount,
  }

  return <SolanaContext.Provider value={value}>{children}</SolanaContext.Provider>
}

export const useSolana = () => useContext(SolanaContext)
